import { useEffect, useMemo, useRef, useState } from 'react';
import { useClient } from 'urql';

import {
  type CompositionV2Filters,
  PAY_V2_QUERY,
  type PayV2Req,
  PositionsEntityKind,
} from '@revelio/data-access';

import type { SalaryToggles } from '../IndividualPotentialPage';
import {
  buildCustomRoleFilter,
  type ResolvedProfileIds,
  type RoleLevel,
} from './useResolveProfileIds';
import {
  BUCKET_SIZE,
  buildDistributionFromHistogram,
  type DistributionPoint,
} from './useSalaryDistribution';

export interface OverlayCompany {
  rcid: number;
  name: string;
  source: 'work_history' | 'competitor';
}

export interface OverlayDistribution {
  companyRcid: number;
  companyName: string;
  distribution: DistributionPoint[];
}

/** Normalize KDE values so distributions from differently-sized companies are comparable. */
const normalizeDistribution = (
  distribution: DistributionPoint[]
): DistributionPoint[] => {
  const total = distribution.reduce((sum, d) => sum + d.kde, 0);
  if (total === 0) return distribution;
  return distribution.map((d) => ({
    value: d.value,
    kde: d.kde / total,
    cdf: d.cdf,
  }));
};

/**
 * Pick the best role level for overlay comparison filters.
 *
 * Company-level salary data is sparse at very granular role levels
 * (k15000 / k1500). Using k150 gives us enough signal to differentiate
 * distributions across companies while still being role-relevant.
 */
const pickOverlayRoleLevel = (roleLevels: RoleLevel[]): RoleLevel | null => {
  // Preference: role_k150 > role_k1500 > role_k7 > role_k15000
  return (
    roleLevels.find((r) => r.type === 'role_k150') ??
    roleLevels.find((r) => r.type === 'role_k1500') ??
    roleLevels.find((r) => r.type === 'role_k7') ??
    roleLevels.find((r) => r.type === 'role_k15000') ??
    null
  );
};

interface CompanyToFetch {
  rcid: number;
  name: string;
}

/** Build the shared filters used for all company-entity overlay queries. */
const buildOverlayFilters = (
  toggles: SalaryToggles,
  overlayRole: RoleLevel | null,
  seniorityId: number | null
): CompositionV2Filters => {
  const filters: CompositionV2Filters = { raw: false };

  if (toggles.role && overlayRole) {
    filters.custom_role = buildCustomRoleFilter(overlayRole);
  }

  if (toggles.seniority && seniorityId) {
    filters.seniority = [seniorityId];
  }

  // NOTE: geo is intentionally NOT applied for overlay comparisons.
  // Company pay data at city/MSA level is too sparse — the API returns
  // an identical smoothed distribution for every company when geo is applied.

  return filters;
};

/**
 * Fetches overlay salary distributions for comparison companies.
 *
 * Each company is queried as a **company entity** with role (at k150)
 * and seniority as filters. When overlays are active, the user's own
 * company is also fetched with the **same** query structure so both
 * primary and overlays are truly comparable.
 *
 * Returns `primaryOverride` when overlays exist — the caller should
 * display this instead of the main `useSalaryDistribution` result so
 * the chart compares like-for-like.
 */
export const useOverlayDistributions = (
  selectedOverlays: OverlayCompany[],
  resolvedIds: ResolvedProfileIds,
  toggles: SalaryToggles,
  _selectedGeo: unknown,
  _selectedRole: RoleLevel | null
): {
  overlayDistributions: OverlayDistribution[];
  /** The user's own company distribution fetched with the same overlay query
   *  structure so charts are directly comparable. Null when no overlays. */
  primaryOverride: OverlayDistribution | null;
  isLoading: boolean;
} => {
  const gqlClient = useClient();
  const [overlayDistributions, setOverlayDistributions] = useState<
    OverlayDistribution[]
  >([]);
  const [primaryOverride, setPrimaryOverride] =
    useState<OverlayDistribution | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const overlayRole = useMemo(
    () => pickOverlayRoleLevel(resolvedIds.roleLevels),
    [resolvedIds.roleLevels]
  );

  // Stable dependency keys
  const overlayIdsKey = useMemo(
    () => selectedOverlays.map((o) => o.rcid).join(','),
    [selectedOverlays]
  );

  const roleFilterKey = overlayRole
    ? `${overlayRole.type}-${overlayRole.id}`
    : '';
  const seniorityKey = String(resolvedIds.seniorityId ?? '');
  const toggleRoleOn = toggles.role;
  const toggleSeniorityOn = toggles.seniority;
  const primaryRcid = resolvedIds.companyRcid;
  const primaryCompanyName = resolvedIds.companyName;

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    // Immediately clear when no overlays are selected
    if (selectedOverlays.length === 0) {
      setOverlayDistributions([]);
      setPrimaryOverride(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const fetchAll = async () => {
      try {
        const filters = buildOverlayFilters(
          toggles,
          overlayRole,
          resolvedIds.seniorityId
        );

        // Build list: user's company first (for primaryOverride), then overlays
        const allCompanies: CompanyToFetch[] = [];

        if (primaryRcid) {
          allCompanies.push({
            rcid: primaryRcid,
            name: primaryCompanyName ?? 'Your Company',
          });
        }

        for (const overlay of selectedOverlays) {
          allCompanies.push({ rcid: overlay.rcid, name: overlay.name });
        }

        const results = await Promise.all(
          allCompanies.map(async (company) => {
            const req: PayV2Req = {
              entities: [
                { EntID: company.rcid, EntType: PositionsEntityKind.Company },
              ],
              filters,
            };

            const result = await gqlClient
              .query(PAY_V2_QUERY, { req })
              .toPromise();

            if (cancelled) return null;

            if (result.error) {
              return null;
            }

            const firstEntity = result.data?.pay_v2?.entities?.[0];
            if (!firstEntity) return null;

            const histogramData = firstEntity.metrics?.histogram?.base;
            if (!histogramData || histogramData.length === 0) return null;

            const distribution = normalizeDistribution(
              buildDistributionFromHistogram(histogramData, BUCKET_SIZE)
            );

            if (distribution.length === 0) return null;

            return {
              companyRcid: company.rcid,
              companyName: company.name,
              distribution,
            };
          })
        );

        if (!cancelled && mountedRef.current) {
          // First result is the primary company (if we had a primaryRcid)
          let newPrimaryOverride: OverlayDistribution | null = null;
          const overlays: OverlayDistribution[] = [];

          for (let i = 0; i < results.length; i++) {
            const r = results[i];
            if (!r) continue;

            if (i === 0 && primaryRcid && r.companyRcid === primaryRcid) {
              newPrimaryOverride = r;
            } else {
              overlays.push(r);
            }
          }

          setPrimaryOverride(newPrimaryOverride);
          setOverlayDistributions(overlays);
        }
      } catch {
        if (!cancelled && mountedRef.current) {
          setOverlayDistributions([]);
          setPrimaryOverride(null);
        }
      } finally {
        if (!cancelled && mountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    fetchAll();

    return () => {
      cancelled = true;
    };
  }, [
    overlayIdsKey,
    roleFilterKey,
    seniorityKey,
    toggleRoleOn,
    toggleSeniorityOn,
    primaryRcid,
    gqlClient,
  ]);

  return { overlayDistributions, primaryOverride, isLoading };
};
